import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/fileshare/context/AuthContext";
import { Share2, LogOut, LayoutDashboard, Settings as SettingsIcon, Inbox } from "lucide-react";
import { Button } from "@/fileshare/components/ui/button";

const NavLink = ({ to, children, testid }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium transition-colors ${
        active ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </Link>
  );
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isBusiness = user && user.tier === "business";

  const handleLogout = async () => {
    await logout();
    navigate("/fileshares");
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-xl border-b border-slate-100 pt-[env(safe-area-inset-top)]">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/fileshares" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-blue-500 flex items-center justify-center">
            <Share2 className="h-4 w-4 text-white" strokeWidth={2} />
          </div>
          <span className="text-[17px] font-bold tracking-tight text-slate-900">FileShare</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/fileshares" testid="nav-home">Upload</NavLink>
          <NavLink to="/fileshares/pricing" testid="nav-pricing">Pricing</NavLink>
          {user && (
            <NavLink to="/fileshares/dashboard" testid="nav-dashboard">Dashboard</NavLink>
          )}
          {isBusiness && (
            <NavLink to="/fileshares/file-requests" testid="nav-file-requests">File Requests</NavLink>
          )}
          {user && user.is_admin && (
            <NavLink to="/fileshares/admin" testid="nav-admin">Admin</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/fileshares/settings" data-testid="nav-settings">
                <Button variant="ghost" size="sm" className="text-slate-600">
                  <SettingsIcon className="h-4 w-4" />
                </Button>
              </Link>
              <Button
                data-testid="nav-logout-btn"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-600 gap-1.5"
              >
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/fileshares/login" data-testid="nav-login">
                <Button variant="ghost" size="sm" className="text-slate-600">Log in</Button>
              </Link>
              <Link to="/fileshares/signup" data-testid="nav-signup">
                <motion.span whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-block">
                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white">Sign up</Button>
                </motion.span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
